import React, { createContext, useState, useCallback, ReactNode } from 'react';

export interface ErrorNotification {
  id: string;
  message: string;
  error_id?: string | number;
  timestamp: number;
  dismissed: boolean;
}

interface ErrorNotificationContextType {
  errors: ErrorNotification[];
  addError: (message: string, error_id?: string | number) => string;
  dismissError: (id: string) => void;
  clearAllErrors: () => void;
}

export const ErrorNotificationContext = createContext<ErrorNotificationContextType>({
  errors: [],
  addError: () => '',
  dismissError: () => {},
  clearAllErrors: () => {},
});

interface ErrorNotificationProviderProps {
  children: ReactNode;
}

export const ErrorNotificationProvider: React.FC<ErrorNotificationProviderProps> = ({ children }) => {
  const [errors, setErrors] = useState<ErrorNotification[]>([]);

  const addError = useCallback((message: string, error_id?: string | number): string => {
    const id = `error-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const notification: ErrorNotification = {
      id,
      message,
      error_id,
      timestamp: Date.now(),
      dismissed: false,
    };
    setErrors((prev) => [...prev, notification]);
    return id;
  }, []);

  const dismissError = useCallback((id: string) => {
    setErrors((prev) =>
      prev.map((error) =>
        error.id === id ? { ...error, dismissed: true } : error
      )
    );
    setTimeout(() => {
      setErrors((prev) => prev.filter((error) => error.id !== id));
    }, 300);
  }, []);

  const clearAllErrors = useCallback(() => {
    setErrors([]);
  }, []);

  return (
    <ErrorNotificationContext.Provider
      value={{
        errors: errors.filter((e) => !e.dismissed),
        addError,
        dismissError,
        clearAllErrors,
      }}
    >
      {children}
    </ErrorNotificationContext.Provider>
  );
};

export const useErrorNotification = () => {
  const context = React.useContext(ErrorNotificationContext);
  if (!context) {
    throw new Error('useErrorNotification must be used within ErrorNotificationProvider');
  }
  return context;
};
